// All user-visible text lives here, per UI language. English is the primary language;
// every other language must provide exactly the same keys (enforced by the `Strings` type).
import type { Lang } from "../src/languages";
import type { ThemeId } from "../src/themes";
import { MAX_PLAYERS } from "../src/protocol";
import { LANG } from "./i18n";

const en = {
  appName: "WordsBrawl",
  pageTitle: "WordsBrawl — Team word search",
  tagline: "The real-time team word search",
  homeIntro: "Create a room, share the link and race your friends to find the words first.",

  nameLabel: "Your name",
  namePlaceholder: "e.g. Alex",
  nameInvalid: "Enter a name of 1 to 20 characters, without < > & \" '.",
  themeLabel: "Theme",
  themeRandom: "🎲 Random",
  wordLanguageLabel: "Word language",
  durationLabel: "Round length",
  roundsLabel: "Rounds",
  roundsOption: (n: number) => (n === 1 ? "1 round" : `${n} rounds, with final standings`),
  minutes: (n: number) => `${n} minutes`,
  uiLanguageLabel: "Language",
  playTitle: "New room",
  createRoom: "Create room",
  creatingRoom: "Creating…",
  createFailed: "Couldn't create the room. Please try again.",
  or: "or",
  codeLabel: "Room code",
  codePlaceholder: "ABCDE",
  codeInvalid: "Room codes have 5 characters (letters and numbers).",
  join: "Join",
  verifying: "Verifying…",
  joinFailed: "Couldn't check the room. Please try again.",
  turnstileFailed: "We couldn't verify you're a person. Please try again.",
  turnstileUnavailable: "The security check couldn't load. Check your connection and reload the page.",
  turnstileInteract: "Tick the box below to confirm you're a person.",

  namePromptTitle: "What's your name?",
  namePromptBody: "This is how the others will see you on the scoreboard.",
  enterRoom: "Enter room",

  room: "Room",
  copyLink: "Copy link",
  linkCopied: "Copied!",
  linkCopiedToast: "Link copied",
  copyFailed: "Copy this link:",
  copyFailedShort: "Couldn't copy",
  share: "Share",
  shareVia: "Share",
  shareText: "Join me on WordsBrawl, the team word search!",
  close: "Close",
  cancel: "Cancel",
  exit: "Leave",
  exitTitle: "Leave the room?",
  exitBody: "You'll go back to the home screen. Come back with the room link to keep your name and score.",
  theme: "Theme",
  words: "Words",
  scoreboard: "Scoreboard",
  you: "you",
  offline: "offline",
  points: (n: number) => `${n} ${n === 1 ? "point" : "points"}`,
  wordsProgress: (found: number, total: number) => `${found} of ${total}`,
  boardLabel: "Word search board",
  wordsIn: (language: string) => `Words in ${language}`,

  statusConnecting: "Connecting…",
  statusReconnecting: "Reconnecting…",
  statusStopped: "Connection lost. Reload the page to rejoin.",
  gameFinished: "All the words have been found!",
  timeUp: "Time's up!",
  viewResults: "See results",
  resultsTitle: "Round over!",
  youWon: "You won! 🎉",
  winner: (name: string) => `${name} wins!`,
  tie: "It's a tie!",
  rematch: "Rematch",
  rematchTheme: "Rematch theme",
  rematchPending: "Getting ready…",
  viewBoard: "See board",
  round: (n: number) => `Round ${n}`,
  roundOf: (n: number, total: number) => `Round ${n} of ${total}`,
  roundDone: (n: number, total: number) => `Round ${n} of ${total} done`,
  finalResults: "Final results",
  seriesYouWon: "You won the series! 🏆",
  seriesWinner: (name: string) => `${name} wins the series!`,
  seriesTie: "The series ends in a tie!",
  standingsSoFar: "Standings so far",
  standingsTitle: "Points per round",
  roundColumn: (n: number) => `R${n}`,
  totalColumn: "Total",
  nextRound: (n: number, total: number) => `Next round (${n} of ${total})`,
  nextRoundTheme: "Next round's theme",
  newSeries: "New series",
  timeLeft: "Time left",
  waitingTitle: "Ready when you are",
  waitingBody: (n: number) => (n === 1 ? "You're the only one here. Share the link, or start on your own." : `${n} people in the room.`),
  waitingHint: "The board and the words appear when the round starts.",
  startRound: "Start round",
  getReady: "Get ready",
  newRound: (theme: string) => `New round! Theme: ${theme}`,
  themeChanged: (theme: string) => `New theme: ${theme}`,
  themeLocked: "The theme can only change before the first word is found.",

  qrTitle: "Invite your team",
  qrLabel: (code: string) => `QR code to join room ${code}`,

  errorRoomNotFound: "This room doesn't exist or has expired.",
  errorRoomFull: `This room is full (${MAX_PLAYERS} people max).`,
  errorGeneric: "Something went wrong.",
  invalidRoomLink: "That room link isn't valid.",
  backHome: "Back to home",
  backToHub: "Back to WorkBrawl",

  privacy: "Privacy",
  terms: "Terms",
  consentText: "We use analytics cookies to learn how WorkBrawl is used. They\'re off unless you accept.",
  consentAccept: "Accept",
  consentReject: "Reject",
  consentNotice: "We use Google Analytics cookies to understand how WorkBrawl is used and improve it.",
  consentGotIt: "Got it",

  // Keep in sync with the static, crawlable copy in public/index.html.
  howTitle: "How it works",
  howSteps: [
    { title: "Create a room.", text: "Pick a theme, the language of the words, the round length and how many rounds, then share the link or the QR code." },
    { title: "Find the words.", text: "Drag across the letters in any of the 8 directions. Everyone sees the same board in real time." },
    { title: "Be the first.", text: "The first person to find a word scores its letters. Most points wins — then hit Rematch for a new board." },
  ],
  faqTitle: "Frequently asked questions",
  faq: [
    { q: "Is WordsBrawl free?", a: "Yes. It's free, needs no account or download and runs in any modern browser." },
    { q: "How many people can join?", a: `Up to ${MAX_PLAYERS} people per room, each on their own phone, tablet or computer.` },
    { q: "Which languages are available?", a: "WordsBrawl and its words are in English and Spanish, with 16 themes of 100 words each." },
    { q: "Can I use it on my phone?", a: "Yes. Just drag your finger across the letters; the selection snaps to the nearest direction." },
  ],

  themes: {
    animals: "Animals",
    weather: "Weather",
    colors: "Colors",
    body: "Human body",
    sports: "Sports",
    school: "School",
    fruits: "Fruits",
    home: "Home",
    music: "Music",
    nature: "Nature",
    countries: "Countries",
    jobs: "Jobs",
    coding: "Coding",
    clothes: "Clothes",
    tech: "Technology",
    transport: "Transport",
  } satisfies Record<ThemeId, string>,
};

type Strings = typeof en;

const es: Strings = {
  appName: "WordsBrawl",
  pageTitle: "WordsBrawl — Sopa de letras en equipo",
  tagline: "La sopa de letras en equipo, en tiempo real",
  homeIntro: "Crea una sala, comparte el link y compite por encontrar las palabras antes que los demás.",

  nameLabel: "Tu nombre",
  namePlaceholder: "Ej. Ana",
  nameInvalid: "Escribe un nombre de 1 a 20 caracteres, sin < > & \" '.",
  themeLabel: "Temática",
  themeRandom: "🎲 Aleatoria",
  wordLanguageLabel: "Idioma de las palabras",
  durationLabel: "Duración de la ronda",
  roundsLabel: "Rondas",
  roundsOption: (n: number) => (n === 1 ? "1 ronda" : `${n} rondas, con resultado final`),
  minutes: (n: number) => `${n} minutos`,
  uiLanguageLabel: "Idioma",
  playTitle: "Nueva sala",
  createRoom: "Crear sala",
  creatingRoom: "Creando…",
  createFailed: "No se pudo crear la sala. Inténtalo de nuevo.",
  or: "o",
  codeLabel: "Código de sala",
  codePlaceholder: "ABCDE",
  codeInvalid: "El código tiene 5 caracteres (letras y números).",
  join: "Unirse",
  verifying: "Verificando…",
  joinFailed: "No se pudo comprobar la sala. Inténtalo de nuevo.",
  turnstileFailed: "No pudimos verificar que eres una persona. Inténtalo de nuevo.",
  turnstileUnavailable: "No se pudo cargar la verificación de seguridad. Revisa tu conexión y recarga la página.",
  turnstileInteract: "Marca la casilla de abajo para confirmar que eres una persona.",

  namePromptTitle: "¿Cómo te llamas?",
  namePromptBody: "Así te verán los demás en el marcador.",
  enterRoom: "Entrar a la sala",

  room: "Sala",
  copyLink: "Copiar link",
  linkCopied: "¡Copiado!",
  linkCopiedToast: "Link copiado",
  copyFailed: "Copia este link:",
  copyFailedShort: "No se pudo copiar",
  share: "Compartir",
  shareVia: "Compartir",
  shareText: "¡Únete conmigo a WordsBrawl, la sopa de letras en equipo!",
  close: "Cerrar",
  cancel: "Cancelar",
  exit: "Salir",
  exitTitle: "¿Salir de la sala?",
  exitBody: "Volverás al inicio. Si regresas con el link de la sala, conservarás tu nombre y puntaje.",
  theme: "Temática",
  words: "Palabras",
  scoreboard: "Marcador",
  you: "tú",
  offline: "desconectado",
  points: (n: number) => `${n} ${n === 1 ? "punto" : "puntos"}`,
  wordsProgress: (found: number, total: number) => `${found} de ${total}`,
  boardLabel: "Tablero de la sopa de letras",
  wordsIn: (language: string) => `Palabras en ${language}`,

  statusConnecting: "Conectando…",
  statusReconnecting: "Reconectando…",
  statusStopped: "Se perdió la conexión. Recarga la página para volver a entrar.",
  gameFinished: "¡Se encontraron todas las palabras!",
  timeUp: "¡Se acabó el tiempo!",
  viewResults: "Ver resultados",
  resultsTitle: "¡Ronda terminada!",
  youWon: "¡Ganaste! 🎉",
  winner: (name: string) => `¡Ganó ${name}!`,
  tie: "¡Empate!",
  rematch: "Revancha",
  rematchTheme: "Temática de la revancha",
  rematchPending: "Preparando…",
  viewBoard: "Ver tablero",
  round: (n: number) => `Ronda ${n}`,
  roundOf: (n: number, total: number) => `Ronda ${n} de ${total}`,
  roundDone: (n: number, total: number) => `Ronda ${n} de ${total} terminada`,
  finalResults: "Resultados finales",
  seriesYouWon: "¡Ganaste la serie! 🏆",
  seriesWinner: (name: string) => `¡${name} gana la serie!`,
  seriesTie: "¡La serie termina en empate!",
  standingsSoFar: "Acumulado hasta ahora",
  standingsTitle: "Puntos por ronda",
  roundColumn: (n: number) => `R${n}`,
  totalColumn: "Total",
  nextRound: (n: number, total: number) => `Siguiente ronda (${n} de ${total})`,
  nextRoundTheme: "Temática de la siguiente ronda",
  newSeries: "Nueva serie",
  timeLeft: "Tiempo restante",
  waitingTitle: "Cuando quieran",
  waitingBody: (n: number) => (n === 1 ? "Por ahora solo estás tú. Comparte el link, o empieza por tu cuenta." : `${n} personas en la sala.`),
  waitingHint: "El tablero y las palabras aparecen al empezar la ronda.",
  startRound: "Empezar ronda",
  getReady: "Prepárate",
  newRound: (theme: string) => `¡Nueva ronda! Temática: ${theme}`,
  themeChanged: (theme: string) => `Nueva temática: ${theme}`,
  themeLocked: "La temática solo se puede cambiar antes de encontrar la primera palabra.",

  qrTitle: "Invita a tu equipo",
  qrLabel: (code: string) => `Código QR para unirse a la sala ${code}`,

  errorRoomNotFound: "Esta sala no existe o ya expiró.",
  errorRoomFull: `La sala está llena (máximo ${MAX_PLAYERS} personas).`,
  errorGeneric: "Ocurrió un error.",
  invalidRoomLink: "El link de la sala no es válido.",
  backHome: "Volver al inicio",
  backToHub: "Volver a WorkBrawl",

  privacy: "Privacidad",
  terms: "Términos",
  consentText: "Usamos cookies de analítica para saber cómo se usa WorkBrawl. Están desactivadas salvo que las aceptes.",
  consentAccept: "Aceptar",
  consentReject: "Rechazar",
  consentNotice: "Usamos cookies de Google Analytics para entender cómo se usa WorkBrawl y mejorarlo.",
  consentGotIt: "Entendido",

  howTitle: "Cómo funciona",
  howSteps: [
    { title: "Crea una sala.", text: "Elige una temática, el idioma de las palabras, la duración de la ronda y cuántas rondas, y comparte el link o el código QR." },
    { title: "Encuentra las palabras.", text: "Arrastra sobre las letras en cualquiera de las 8 direcciones. Todos ven el mismo tablero en tiempo real." },
    { title: "Sé el primero.", text: "Quien encuentra primero una palabra suma sus letras. Gana quien tenga más puntos; luego pulsa Revancha para un tablero nuevo." },
  ],
  faqTitle: "Preguntas frecuentes",
  faq: [
    { q: "¿WordsBrawl es gratis?", a: "Sí. Es gratis, no necesita cuenta ni descarga y funciona en cualquier navegador moderno." },
    { q: "¿Cuántas personas pueden entrar?", a: `Hasta ${MAX_PLAYERS} personas por sala, cada una desde su teléfono, tablet o computadora.` },
    { q: "¿En qué idiomas está?", a: "WordsBrawl y sus palabras están en inglés y español, con 16 temáticas de 100 palabras cada una." },
    { q: "¿Se puede usar en el celular?", a: "Sí. Arrastra el dedo sobre las letras; la selección se ajusta a la dirección más cercana." },
  ],

  themes: {
    animals: "Animales",
    weather: "Clima",
    colors: "Colores",
    body: "Cuerpo humano",
    sports: "Deportes",
    school: "Escuela",
    fruits: "Frutas",
    home: "Hogar",
    music: "Música",
    nature: "Naturaleza",
    countries: "Países",
    jobs: "Profesiones",
    coding: "Programación",
    clothes: "Ropa",
    tech: "Tecnología",
    transport: "Transporte",
  },
};

const ALL: Record<Lang, Strings> = { en, es };

/** Texts for the current UI language. */
export const STRINGS: Strings = ALL[LANG];

/** Each language's name written in that language (used in the language pickers). */
export const LANGUAGE_NAMES: Record<Lang, string> = { en: "English", es: "Español" };

/** A language's name written in the current UI language (e.g. "inglés" for "en" in Spanish). */
export function languageName(lang: Lang): string {
  try {
    const name = new Intl.DisplayNames([LANG], { type: "language" }).of(lang);
    if (name) return name;
  } catch {
    // Intl.DisplayNames unavailable: fall back to the native name.
  }
  return LANGUAGE_NAMES[lang];
}
